import { useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, MapPin, Search, Building2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { StoreHeader } from "../components/StoreHeader";
import { NetworkMap } from "../components/NetworkMap";
import { StateDeepDive } from "../components/StateDeepDive";
import { PartnerSpotlight } from "../components/PartnerSpotlight";
import { PARTNERS, CATEGORIES, ALL_STATES, NETWORK_STATS } from "../lib/network";

const CAT_COLORS = {
  "Elite Super Stockist": "#E8C15A",
  "Super Stockist": "#B87333",
  "Wholesaler": "#2FB999",
  "Dealer": "#5B8DEF",
  "Booking Agent": "#D8B23E",
  "Civil Contractor": "#C0654E",
  "Supplier": "#9B7BD8",
};

const Stat = ({ n, label }) => (
  <div>
    <div className="font-serif text-4xl md:text-5xl text-[#F5F5F0]">{n}</div>
    <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-1">{label}</div>
  </div>
);

export default function PartnerNetwork() {
  const [cat, setCat] = useState("All");
  const [state, setState] = useState("All");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(60);
  const gridRef = useRef(null);
  const deepDiveRef = useRef(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return PARTNERS.filter((p) => {
      if (cat !== "All" && p.category !== cat) return false;
      if (state !== "All" && p.state !== state) return false;
      if (query && !(`${p.name} ${p.city} ${p.state}`.toLowerCase().includes(query))) return false;
      return true;
    });
  }, [cat, state, q]);

  const visible = filtered.slice(0, limit);
  const reset = (fn) => (v) => { fn(v); setLimit(60); };

  const selectFromMap = (s) => {
    setState(s);
    setLimit(60);
    if (s !== "All") {
      setTimeout(() => deepDiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <StoreHeader />

      {/* Hero */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-14 pb-10">
        <Link to="/" className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#B87333] transition-colors">
          <ArrowLeft size={13} /> Back home
        </Link>
        <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-10 lg:gap-16 items-start mt-6">
          <div>
            <span className="overline text-[#B87333] block">/ PAN-India Partner Network</span>
            <h1 className="font-serif text-4xl md:text-6xl mt-3 tracking-[-0.02em] max-w-3xl">A network built on trust &amp; performance.</h1>
            <p className="font-body text-[#8A8A82] mt-4 max-w-2xl leading-relaxed">
              Vinci Inc. is backed by a deep, credit-worthy channel of super stockists, wholesalers, dealers, booking agents,
              civil contractors and suppliers across the country — the foundation of our reliability at scale.
            </p>
          </div>
          <PartnerSpotlight />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12 border-t border-white/10 pt-10">
          <Stat n={`${NETWORK_STATS.total}+`} label="Affiliated partners" />
          <Stat n={NETWORK_STATS.states} label="States & UTs" />
          <Stat n={NETWORK_STATS.categories} label="Partner categories" />
          <Stat n={`${NETWORK_STATS.earliest}`} label="Trusted since" />
        </div>
      </section>

      {/* Interactive coverage map */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-10 border-t border-white/10">
        <NetworkMap activeState={state} onSelect={selectFromMap} />
        {state !== "All" && (
          <div ref={deepDiveRef}>
            <StateDeepDive
              state={state}
              onViewAll={() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              onClose={() => setState("All")}
            />
          </div>
        )}
      </section>

      {/* Filters */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 sticky top-[64px] z-20 bg-[#070707]/90 backdrop-blur py-4 border-y border-white/10">
        <div className="flex flex-wrap items-center gap-2">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`net-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => reset(setCat)(c)}
              className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.14em] transition-colors ${cat === c ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
            >
              {c}
            </button>
          ))}
          <div className="flex-1" />
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A82]" />
            <input
              data-testid="net-search"
              value={q}
              onChange={(e) => reset(setQ)(e.target.value)}
              placeholder="Search firm or city…"
              className="bg-transparent border border-white/15 focus:border-[#B87333] outline-none rounded-full pl-9 pr-4 py-2 font-body text-sm text-[#F5F5F0] w-56"
            />
          </div>
          <select
            data-testid="net-state"
            value={state}
            onChange={(e) => reset(setState)(e.target.value)}
            className="bg-transparent border border-white/15 focus:border-[#B87333] outline-none rounded-full px-4 py-2 font-body text-sm text-[#F5F5F0] [&>option]:bg-[#0e0e0e]"
          >
            <option value="All">All states</option>
            {ALL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </section>

      {/* Grid */}
      <section ref={gridRef} className="max-w-[1500px] mx-auto px-6 md:px-12 py-10">
        <p className="font-body text-sm text-[#8A8A82] mb-6" data-testid="net-count">
          Showing <span className="text-[#F5F5F0]">{visible.length}</span> of {filtered.length} partners
          {state !== "All" && <span className="text-[#B87333]"> · {state}</span>}
        </p>
        {filtered.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="net-empty">No partners match your filters.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="net-grid">
            {visible.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid="net-card"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: (i % 12) * 0.02 }}
                className={`border rounded-sm p-5 transition-colors ${p.featured ? "border-[#B87333]/60 bg-[#B87333]/[0.06] hover:bg-[#B87333]/[0.1]" : "border-white/10 hover:border-[#B87333]/50 hover:bg-white/[0.02]"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-[#141414] border border-white/10 text-[#B87333] shrink-0"><Building2 size={16} /></span>
                  {p.verified && <BadgeCheck size={16} className="text-[#2FB999] mt-1" title="Verified affiliate" />}
                </div>
                {p.elite
                  ? <span className="inline-flex items-center gap-1 mt-3 font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#E8C15A] border border-[#E8C15A]/50 rounded-full px-2.5 py-0.5">★ Elite Super Stockist</span>
                  : p.featured && <span className="inline-block mt-3 font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#B87333] border border-[#B87333]/40 rounded-full px-2.5 py-0.5">Featured partner</span>}
                <div className="font-serif text-lg mt-2 leading-snug">{p.name}</div>
                {p.unit && <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">{p.unit}</div>}
                {p.trade && <div className="font-body text-xs text-[#B87333] mt-1">Trade name · {p.trade}</div>}
                {p.scope && <div className="font-body text-xs text-[#C9C9C2] mt-1.5">{p.scope}</div>}
                <div className="flex items-center gap-1.5 font-body text-xs text-[#8A8A82] mt-2">
                  <MapPin size={12} /> {p.city}, {p.state}
                </div>
                {p.gstin && <div className="font-grotesk text-[10px] uppercase tracking-[0.1em] text-[#C9C9C2] mt-2">GSTIN · {p.gstin}</div>}
                {p.address && <div className="font-body text-[11px] text-[#6f6f68] mt-1 leading-relaxed">{p.address}</div>}
                {p.licensed && <div className="font-grotesk text-[9px] uppercase tracking-[0.14em] text-[#8A8A82] mt-2">{p.licensed}</div>}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full" style={{ color: CAT_COLORS[p.category], border: `1px solid ${CAT_COLORS[p.category]}44` }}>
                    {p.category}
                  </span>
                  <span className="font-body text-[10px] text-[#6f6f68]">Since {p.since}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {limit < filtered.length && (
          <div className="text-center mt-10">
            <button
              data-testid="net-load-more"
              onClick={() => setLimit((l) => l + 60)}
              className="rounded-full border border-white/20 px-8 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] hover:bg-[#B87333] hover:text-black transition-colors"
            >
              Load more ({filtered.length - limit} more)
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
